import FormatCurrency from 'components/Common/FormatCurrency'
import LineCheckIcon from 'components/Luxkit/Icons/line/LineCheckIcon'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import React from 'react'
import styled from 'styled-components'
import LineBedIcon from 'components/Luxkit/Icons/line/LineBedIcon'
import LineChairWithAirplaneIcon from 'components/Luxkit/Icons/line/LineChairWithAirplaneIcon'
import LineCompassIcon from 'components/Luxkit/Icons/line/LineCompassIcon'
import { rem } from 'polished'
import LineCameraIcon from 'components/Luxkit/Icons/line/LineCameraIcon'
import LineShieldCheckIcon from 'components/Luxkit/Icons/line/LineShieldCheckIcon'
import LineTaxiIcon from 'components/Luxkit/Icons/line/LineTaxiIcon'
import LinePhoneIcon from 'components/Luxkit/Icons/line/LinePhoneIcon'
import { setSvgIconSize } from 'components/Luxkit/SvgIcon'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { mediaQueryUp } from 'components/utils/breakpoint'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

const IconBadge = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.one};
  border-radius: ${props => props.theme.borderRadius.round};
  color: ${props => props.theme.palette.neutral.default.eight};
  height: ${rem(32)};
  width: ${rem(32)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const BenefitsGrid = styled.div`
  display: grid;
  row-gap: ${rem(16)};
  ${setSvgIconSize(16)}
  position: relative;

  ${mediaQueryUp.tablet} {
    ${setSvgIconSize(20)}
  }
`

const BenefitRow = styled.div`
  display: grid;
  column-gap: ${rem(24)};
  grid-template-columns: 1fr repeat(4, ${rem(96)});
  padding-bottom: ${rem(12)};
  z-index: 1;

  &.bordered  {
    border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};
  }
`

const BenefitsRowCondensed = styled.div`
  display: grid;
  column-gap: ${rem(4)};
  grid-template-columns: 1fr repeat(4, ${rem(54)});
  padding-bottom: ${rem(12)};
  z-index: 1;
  align-items: center;

  &.bordered  {
    border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};
  }
`

const HighlightElement = styled.div`
  position: absolute;
  top: -${rem(8)};
  bottom: 0;
  width: ${rem(54)};

  &.highlight-platinum {
    right: 0;
    background-color: ${props => props.theme.palette.product.loyalty.platinum.normalBackground};
  }

  &.highlight-gold {
    right: ${rem(54 + 4)};
    background-color: ${props => props.theme.palette.product.loyalty.gold.normalBackground};
  }

  &.highlight-silver {
    right: ${rem((54 + 4) * 2)};
    background-color: ${props => props.theme.palette.product.loyalty.silver.normalBackground};
  }

  &.highlight-bronze {
    right: ${rem((54 + 4) * 3)};
    background-color: ${props => props.theme.palette.product.loyalty.bronze.normalBackground};
  }

  ${mediaQueryUp.desktop} {
    width: ${rem(96)};

    &.highlight-platinum {
      right: 0;
    }

    &.highlight-gold {
      right: ${rem(96 + 24)};
    }

    &.highlight-silver {
      right: ${rem((96 + 24) * 2)};
    }

    &.highlight-bronze {
      right: ${rem((96 + 24) * 3)};
    }
  }
`

interface Benefit {
  title: string;
  /**
   * Smaller title for mobile screens
   */
  abbrvTitle?: string;
  bronze: React.ReactNode;
  silver: React.ReactNode;
  gold: React.ReactNode;
  platinum: React.ReactNode;
  icon: React.ReactNode;
}

const benefits: Array<Benefit> = [
  { title: 'Free airport chauffeur service', bronze: '-', silver: '-', gold: '2', platinum: 'Unlimited', icon: <LineTaxiIcon /> },
  { title: 'Airport lounge passes', bronze: '-', silver: '2', gold: '2', platinum: '2', icon: <LineChairWithAirplaneIcon /> },
  { title: 'Guaranteed free room upgrades', bronze: '-', silver: '-', gold: '1', platinum: '4', icon: <LineBedIcon /> },
  { title: 'Earn points on every booking', abbrvTitle: 'Earn points', bronze: <LineCheckIcon />, silver: <LineCheckIcon />, gold: <LineCheckIcon />, platinum: <LineCheckIcon />, icon: <LineBedIcon /> },
  { title: 'Use points with no restrictions', abbrvTitle: 'Use points', bronze: <LineCheckIcon />, silver: <LineCheckIcon />, gold: <LineCheckIcon />, platinum: <LineCheckIcon />, icon: <LineBedIcon /> },
  { title: 'Tour optional extra credits', bronze: '-', silver: '1x $100', gold: '2x $250', platinum: 400, icon: <LineCompassIcon /> },
  { title: 'TravelShoot voucher', bronze: '-', silver: 200, gold: 200, platinum: 200, icon: <LineCameraIcon /> },
  { title: 'Travel protection discount', bronze: '-', silver: '-', gold: '15%', platinum: '25%', icon: <LineShieldCheckIcon /> },
  { title: 'Priority phone support 24/7 ', bronze: '-', silver: <LineCheckIcon />, gold: <LineCheckIcon />, platinum: <LineCheckIcon />, icon: <LinePhoneIcon /> },
]

function getTextColour(targetTier: App.LoyaltyTier, currentTier?: App.LoyaltyTier): `tier-${App.LoyaltyTier}-contrast` | undefined {
  if (targetTier === currentTier) {
    return `tier-${targetTier}-contrast`
  }
}

interface Props {
  className?: string;
  /**
   * Highlights the tiers row given (sets a background)
   */
  highlightTier?: App.LoyaltyTier;
}

function LoyaltyLandingPageBenefitsTable(props: Props) {
  const { className, highlightTier } = props

  return <>
    <CSSBreakpoint min="desktop" as={BenefitsGrid} className={className}>
      {highlightTier && <HighlightElement className={`highlight-${highlightTier}`} />}
      <BenefitRow>
        <Heading variant="heading3"><i>Benefits</i> at a glance</Heading>
        <Heading variant="heading5" colour={getTextColour('bronze', highlightTier) ?? 'tier-bronze'} align="center">Bronze</Heading>
        <Heading variant="heading5" colour={getTextColour('silver', highlightTier) ?? 'tier-silver'} align="center">Silver</Heading>
        <Heading variant="heading5" colour={getTextColour('gold', highlightTier) ?? 'tier-gold'} align="center">Gold</Heading>
        <Heading variant="heading5" colour={getTextColour('platinum', highlightTier) ?? 'tier-platinum'} align="center">Platinum</Heading>
      </BenefitRow>
      {benefits.map(benefit => <BenefitRow className="bordered" key={benefit.title}>
        <Group direction="horizontal" gap={8} verticalAlign="center">
          <IconBadge>{benefit.icon}</IconBadge>
          <BodyText variant="large">{benefit.title}</BodyText>
        </Group>
        <BodyText variant="large" weight="bold" align="center" colour={getTextColour('bronze', highlightTier)}>
          {typeof benefit.bronze === 'number' ? <FormatCurrency format="dynamic" value={benefit.bronze} /> : benefit.bronze}
        </BodyText>
        <BodyText variant="large" weight="bold" align="center" colour={getTextColour('silver', highlightTier)}>
          {typeof benefit.silver === 'number' ? <FormatCurrency format="dynamic" value={benefit.silver} /> : benefit.silver}
        </BodyText>
        <BodyText variant="large" weight="bold" align="center" colour={getTextColour('gold', highlightTier)} >
          {typeof benefit.gold === 'number' ? <FormatCurrency format="dynamic" value={benefit.gold} /> : benefit.gold}
        </BodyText>
        <BodyText variant="large" weight="bold" align="center" colour={getTextColour('platinum', highlightTier)}>
          {typeof benefit.platinum === 'number' ? <FormatCurrency format="dynamic" value={benefit.platinum} /> : benefit.platinum}
        </BodyText>
      </BenefitRow>)}
    </CSSBreakpoint>
    <CSSBreakpoint max="tablet" className={props.className}>
      <VerticalSpacer gap={20}>
        <Heading variant="heading3"><i>Benefits</i> at a glance</Heading>
        <BenefitsGrid>
          {highlightTier && <HighlightElement className={`highlight-${highlightTier}`} />}
          <BenefitsRowCondensed>
            <div />
            <Heading variant="heading6" colour={getTextColour('bronze', highlightTier) ?? 'tier-bronze'} align="center">Bronze</Heading>
            <Heading variant="heading6" colour={getTextColour('silver', highlightTier) ?? 'tier-silver'} align="center">Silver</Heading>
            <Heading variant="heading6" colour={getTextColour('gold', highlightTier) ?? 'tier-gold'} align="center">Gold</Heading>
            <Heading variant="heading6" colour={getTextColour('platinum', highlightTier) ?? 'tier-platinum'} align="center">Plat</Heading>
          </BenefitsRowCondensed>
          {benefits.map(benefit => <BenefitsRowCondensed className="bordered" key={benefit.title}>
            <BodyText variant="small">{benefit.abbrvTitle ?? benefit.title}</BodyText>
            <BodyText variant="small" weight="bold" align="center" colour={getTextColour('bronze', highlightTier)}>
              {typeof benefit.bronze === 'number' ? <FormatCurrency format="dynamic" value={benefit.bronze} /> : benefit.bronze}
            </BodyText>
            <BodyText variant="small" weight="bold" align="center" colour={getTextColour('silver', highlightTier)} >
              {typeof benefit.silver === 'number' ? <FormatCurrency format="dynamic" value={benefit.silver} /> : benefit.silver}
            </BodyText>
            <BodyText variant="small" weight="bold" align="center" colour={getTextColour('gold', highlightTier)} >
              {typeof benefit.gold === 'number' ? <FormatCurrency format="dynamic" value={benefit.gold} /> : benefit.gold}
            </BodyText>
            <BodyText variant="small" weight="bold" align="center" colour={getTextColour('platinum', highlightTier)}>
              {typeof benefit.platinum === 'number' ? <FormatCurrency format="dynamic" value={benefit.platinum} /> : benefit.platinum}
            </BodyText>
          </BenefitsRowCondensed>)}
        </BenefitsGrid>
      </VerticalSpacer>
    </CSSBreakpoint>
  </>
}

export default LoyaltyLandingPageBenefitsTable
